import React, { useState } from 'react';
import ButtonSet from './components/ButtonSet';
import InfoSection from './components/InfoSection';
import DataTable from './components/DataTable';
import { useFetchData } from './hooks/useFetchData';
import './App.css';
import { locations } from './locations';
import { format } from 'date-fns'

export const COMMON_DATE = 'yyyy-MM-dd HH:mm'

const App = () => {
  const [source, setSource] = useState('local');
  const { data, isLoading, error } = useFetchData(source === 'local' ? 'https://apifale.krishna.magnaro.com/health_data/out.json' : '/out.json');
  const graylist = data?.graylist;

  if (isLoading) return <p>Ładowanie...</p>;
  if (error) return <p>Wystąpił błąd: {error.message}</p>;

  const systemData = Object.entries(data?.sites || {})
  .filter(([url]) => !data?.blacklist?.sites?.includes(url))
  .map(([url, { success, status, data, error }]) => ({
    url,
    status,
    data: data || error,
    className: graylist?.sites?.includes(url) ? 'grayed' 
    : success ? 'success' : 'error',
  }));

  const buoysData = Object.entries(data?.locations || {})
  .filter(([key]) => !data?.blacklist?.locations?.includes(key))
  .map(([key, { name, current, target, success, distance }]) => ({
    name: name,
    lat: current?.lat,
    lon: current?.lon,
    distance,
    className: graylist?.locations?.includes(key) ? 'grayed'
    : success ? 'success' : 'error',
  }));

  const geoData = Object.entries(data?.geodata || {})
  .filter(([key]) => !data?.blacklist?.geodata?.includes(key))
  .map(([key, {location, data, success, error, severity}]) => ({
    location,
    received: format(data?.received, COMMON_DATE),
    className: graylist?.geodata?.includes(key) ? 'grayed'
    : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
  }));

  const climateData = Object.entries(data?.measure || {})
  .filter(([key]) => !data?.blacklist?.measure?.includes(`${key.split('-')[1]}:clima`))
  .filter(([key, {clima}]) => clima)
  .map(([key, {clima}]) => {
    const data = clima?.data
    const success = clima?.success
    const error = clima?.error
    const severity = clima?.severity
    return {
      location: locations[key],
      received: format(data?.received, COMMON_DATE),
      "Ws": data?.ws ? Number(data?.ws).toFixed(2) + " m/s": "", 
      "We": data?.we? Number(data?.we).toFixed(2) + "°": "",
      "Tp": data?.tp? <>{Number(data?.tp).toFixed(2)}&nbsp;s</>: "",
      "Rh": data?.rh? Number(data?.rh).toFixed(2) + "%": "",
      "Ap": data?.ap? Number(data?.ap).toFixed(2) + " hPa": "",
      "Tpr": data?.tpr? Number(data?.tpr).toFixed(2) + "°C": "",
      className: graylist?.measure?.includes(`${key.split('-')[1]}:clima`) ? 'grayed'
      : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
    }
  });

  const waveData = Object.entries(data?.measure || {})
  .filter(([key]) => !data?.blacklist?.measure?.includes(`${key.split('-')[1]}:wave`))
  .filter(([key, {wave}]) => wave)
  .map(([key, {wave}]) => {
    const data = wave?.data
    const success = wave?.success
    const error = wave?.error
    const severity = wave?.severity
    return {
      location: locations[key],
      received: format(data?.received, COMMON_DATE),
      "Ap": data?.ap? Number(data?.ap).toFixed(2) + " hPa": "",
      "Tp": data?.tp? <>{Number(data?.tp).toFixed(2)}&nbsp;s</>: "",
      "Rh": data?.rh? Number(data?.rh).toFixed(2) + "%": "",
      "Vcc": data?.vcc? Number(data?.vcc).toFixed(2) + " V": "",
      "Hmax": data?.hmax? Number(data?.hmax).toFixed(2) + " m": "",
      "Seal": data?.seal? Number(data?.seal).toFixed(2): "",
      className: graylist?.measure?.includes(`${key.split('-')[1]}:wave`) ? 'grayed'
      : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
    }
  });

  const adcpData = Object.entries(data?.measure || {})
  .filter(([key]) => !data?.blacklist?.measure?.includes(`${key.split('-')[1]}:adcp`))
  .filter(([key, {adcp}]) => adcp)
  .map(([key, {adcp}]) => {
    const data = adcp?.data
    const success = adcp?.success
    const error = adcp?.error
    const severity = adcp?.severity
    return {
      location: locations[key],
      received: format(data?.received, COMMON_DATE),
      "Ap": data?.ap? Number(data?.ap).toFixed(2) + " hPa": "",
      "Tp": data?.tp? <>{Number(data?.tp).toFixed(2)}&nbsp;s</>: "",
      "Rh": data?.rh? Number(data?.rh).toFixed(2) + "%": "",
      "Vcc": data?.vcc? Number(data?.vcc).toFixed(2) + " V": "",
      className: graylist?.measure?.includes(`${key.split('-')[1]}:adcp`) ? 'grayed'
      : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
    }
  })

  const ssumData = Object.entries(data?.measure || {})
  .filter(([key]) => !data?.blacklist?.measure?.includes(`${key.split('-')[1]}:ssum`))
  .filter(([key, {ssum}]) => ssum)
  .map(([key, {ssum}]) => {
    const data = ssum?.data
    const success = ssum?.success
    const error = ssum?.error
    const severity = ssum?.severity
    return {
      location: locations[key],
      received: format(data?.received, COMMON_DATE),
      "Hm0": data?.hm0? Number(data?.hm0).toFixed(2) + " m": "",
      "Tz": data?.tz? Number(data?.tz).toFixed(2) + " s": "",
      "Dirp": data?.dirp? Number(data?.dirp).toFixed(2) + "°": "",
      "Tpw": data?.tpw? Number(data?.tpw).toFixed(2) + "°C": "",
      className: graylist?.measure?.includes(`${key.split('-')[1]}:ssum`) ? 'grayed'
      : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
    }
  })

  const bsumData = Object.entries(data?.measure || {})
  .filter(([key]) => !data?.blacklist?.measure?.includes(`${key.split('-')[1]}:bsum`))
  .filter(([key, {bsum}]) => bsum)
  .map(([key, {bsum}]) => {
    const data = bsum?.data
    const success = bsum?.success
    const error = bsum?.error
    const severity = bsum?.severity
    return {
      location: locations[key],
      received: format(data?.received, COMMON_DATE),
      "Tsea": data?.tsea? Number(data?.tsea).toFixed(2) + "°C": "",
      className: graylist?.measure?.includes(`${key.split('-')[1]}:bsum`) ? 'grayed'
      : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
    }
  })

  const wsumData = Object.entries(data?.measure || {})
  .filter(([key]) => !data?.blacklist?.measure?.includes(`${key.split('-')[1]}:wsum`))
  .filter(([key, {wsum}]) => wsum)
  .map(([key, {wsum}]) => {
    const data = wsum?.data
    const success = wsum?.success
    const error = wsum?.error
    const severity = wsum?.severity
    return {
      location: locations[key],
      received: format(new Date(data?.received), COMMON_DATE),
      "Hmax": data?.hmax? Number(data?.hmax).toFixed(2) + " m": "",
      "THmax": data?.thmax? Number(data?.thmax).toFixed(2) + " s": "",
      className: graylist?.measure?.includes(`${key.split('-')[1]}:wsum`) ? 'grayed'
      : success ? 'success' : severity === "WARN" ? 'warn' : error ? 'error' : '',
    }
  })

  return (
    <div className="container">
      <h1>Przeglądarka danych</h1>
      {/*<ButtonSet onSourceChange={setSource} />*/}

      <InfoSection info={data?.info} />

      <h2>Systemy</h2>
      <DataTable
        data={systemData || []}
        columns={[
          { label: 'URL', key: 'url' },
          { label: 'Status', key: 'status' },
          { label: 'Dane', key: 'data' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h2>Lokalizacje boi</h2>
      <DataTable
        data={buoysData || []}
        columns={[
          { label: 'Nazwa', key: 'name' },
          { label: 'Szerokość', key: 'lat' },
          { label: 'Długość', key: 'lon' },
          { label: 'Odległość', key: 'distance' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h2>Geodane</h2>
      <DataTable
        data={geoData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h2>Pomiary</h2>
      <h3>Stacja meteorologiczna</h3>
      <DataTable
        data={climateData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
          { label: 'Ws', key: 'Ws' },
          { label: 'We', key: 'We' },
          { label: 'Tp', key: 'Tp' },
          { label: 'Rh', key: 'Rh' },
          { label: 'Ap', key: 'Ap' },
          { label: 'Tpr', key: 'Tpr' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h3>Sonda krawędziowa</h3>
      <DataTable
        data={waveData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
          { label: 'Ap', key: 'Ap' },
          { label: 'Tp', key: 'Tp' },
          { label: 'Rh', key: 'Rh' },
          { label: 'Vcc', key: 'Vcc' },
          { label: 'Hmax', key: 'Hmax' },
          { label: 'Seal', key: 'Seal' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h3>ADCP</h3>
      <DataTable
        data={adcpData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
          { label: 'Ap', key: 'Ap' },
          { label: 'Tp', key: 'Tp' },
          { label: 'Rh', key: 'Rh' },
          { label: 'Vcc', key: 'Vcc' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h2>Boja falowa</h2>
      <h3>ssum</h3>
      <DataTable
        data={ssumData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
          { label: 'Hm0', key: 'Hm0' },
          { label: 'Tz', key: 'Tz' },
          { label: 'Dirp', key: 'Dirp' },
          { label: 'Tpw', key: 'Tpw' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h3>bsum</h3>
      <DataTable
        data={bsumData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
          { label: 'Tsea', key: 'Tsea' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

      <h3>wsum</h3>
      <DataTable
        data={wsumData || []}
        columns={[
          { label: 'Lokalizacja', key: 'location' },
          { label: 'Data otrzymania', key: 'received' },
          { label: 'Hmax', key: 'Hmax' },
          { label: 'THmax', key: 'THmax' },
        ]}
        getRowProps={(row) => ({ className: row.className })}
      />

    </div>
  );
};

export default App;
